<template>
  <div class="order-details grid-x">
    <div class="cell small-12 medium-9">
      <BreadcrumbWrapper :current-page="currentPage" class="margin-bottom-xs" />
    </div>
    <div v-if="!isLoading" class="cell small-12 medium-9 small-margin-bottom-xl large-margin-bottom-z">
      <div class="grid-x align-justify">
        <h1
          :class="{
            'small-margin-bottom-s medium-margin-bottom-m': hasNotification,
          }"
        >
          {{ ORDER_DETAILS.orderDetailsHeading }}
        </h1>

        <button v-if="isCancelOrderEligible" class="link-body cancel-order-link" @click="cancelOrder">
          {{ ORDER_DETAILS.cancelOrderText }}
        </button>
      </div>
      <div v-if="hasNotification" class="margin-bottom-m">
        <Notification
          v-if="order?.returnServiceDown"
          :class="{ 'margin-bottom-xs': orderDetails.orderDetailsError }"
          severity="error"
          :closable="false"
        >
          {{ ORDER_DETAILS.notification.returnServiceDownText }}
        </Notification>
        <Notification v-if="orderDetails.orderDetailsError" severity="error" :closable="false">
          <!-- eslint-disable vue/no-v-html -->
          <div v-html="orderDetails.orderDetailsError"></div>
        </Notification>
      </div>
      <template v-if="!isLoading">
        <div class="grid-x order-details-wrapper margin-bottom-m">
          <div class="cell small-12 medium-9 sub-order-detail-wrapper">
            <div class="grid-x">
              <OrderCardHeader
                v-if="orderItems.length"
                :order-number="$route.params.orderNumber || order?.orderNumber"
                :order-date="order?.orderDate"
                page="orderDetails"
                class="margin-top-m"
              />
            </div>
          </div>
        </div>
      </template>
      <div v-if="isBigTicketOrder" class="cell margin-bottom-m">
        <h2 class="h4 margin-bottom-xxs">
          {{ ORDER_DETAILS.bigTicket.questionHeading }}
        </h2>
        <p>{{ ORDER_DETAILS.bigTicket.questionNote(CUSTOMER_SERVICE_BT) }}</p>
      </div>
      <div v-for="(shipment, index) in shipments" :key="index" class="cell margin-bottom-m">
        <h2 class="h4 margin-bottom-xxs">
          {{ shipment.shipmentStatus }}
        </h2>
        <div
          v-if="shipment.shipmentSubStatus"
          class="medium"
          :class="{ 'margin-bottom-xxs': !shipment.shipmentSubStatusDetails }"
          v-text="shipment.shipmentSubStatus"
        ></div>
        <p v-if="shipment.shipmentSubStatusDetails" class="margin-bottom-xxs small gray-1-color" v-text="shipment.shipmentSubStatusDetails"></p>
        <p v-if="isBigTicketOrder && shipment.salesCheckNumber" class="margin-bottom-xxs">
          {{ ORDER_DETAILS.bigTicket.salesCheckNumber(shipment.salesCheckNumber) }}
        </p>

        <div class="order-information-links margin-top-xxs">
          <button
            v-if="shouldShowConfirmChoiceCta(shipment)"
            class="confirm-your-choice-cta link-body"
            @click="confirmChoice(shipment)"
            v-text="`Confirm ${shipment.cancelItemEligibleFlag ? 'your choice' : 'to wait'}`"
          ></button>

          <button v-else-if="shouldShowCancelItemCta(shipment)" class="cancel-item-cta link-body" @click="confirmCancelItem(shipment)">
            {{ ORDER_DETAILS.orderInformation.cancelItemText }}
          </button>
          <button v-else-if="shouldShowScheduleDeliveryCta(shipment)" class="schedule-delivery-cta" @click="scheduleDelivery">
            <span class="link-body">{{ ORDER_DETAILS.orderInformation.scheduleDeliveryText }}</span>
          </button>
          <NuxtLink
            v-else-if="shipment.pendingReturnId"
            class="return-details-cta link-body"
            :to="{ name: 'Return Details', query: { pid: shipment.pendingReturnId, orderNumber: order?.orderNumber } }"
          >
            {{ getReturnDetailsLabel(shipment) }}
          </NuxtLink>
          <NuxtLink
            v-else-if="shouldShowConfirmShippingLink(shipment)"
            class="confirm-shipping link-body"
            :to="shipment?.saveTheSaleUrl"
            external
            target="_blank"
          >
            {{ ORDER_DETAILS.orderInformation.confirmShippingText }}
          </NuxtLink>
          <NuxtLink
            v-else-if="shouldShowTrackPackageLink(shipment)"
            class="track-package-cta link-body"
            :to="{ name: 'Track Package', query: { o: shipment.orderToken, s: shipment.shipmentToken, trackingNos: shipment.trackingNos } }"
            @click="trackPackage"
          >
            {{ getTrackPackageLabel(shipment) }}
          </NuxtLink>
        </div>
        <!-- back-order-confirm -->
        <Notification v-if="shouldShowShippingStatusNotification(shipment)" class="margin-top-xs" severity="success" :closable="false">
          {{ ORDER_DETAILS.notification.shippingStatusText }}
        </Notification>

        <Notification v-if="shouldShowReroutedNotification(shipment)" class="margin-top-xs" severity="info" :closable="false">
          {{ ORDER_DETAILS.notification.accessPointText }}
          <NuxtLink
            v-if="isPackageTrackable(shipment)"
            class="track-package-cta link-body"
            :to="{ name: 'Track Package', query: { o: shipment.orderToken, s: shipment.shipmentToken, trackingNos: shipment.trackingNos } }"
            @click="trackPackage"
          >
            {{ ORDER_DETAILS.notification.trackPackageText }}
          </NuxtLink>
        </Notification>

        <ProductThumbnailWrapper
          show-buy-again
          show-policy-message
          :products="transformProducts(shipment.items)"
          :shipment-type="shipment.shipmentType"
          :gift-message="shipment.giftMessage"
          :return-item-action="(product) => returnItemAction(product, shipment)"
          :is-red-carpet-shipment="isRedCarpetShipment"
          page="order-details"
          :is-buy-again-slideout-enabled="isBuyAgainSlideoutEnabled"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          :loading="index === 0 ? 'eager' : 'lazy'"
        />
      </div>
      <div class="order-details-info cell grid-x">
        <div class="cell padding-right-xxs medium-margin-bottom-m" :class="isS2AP ? 'large-4' : 'large-6'">
          <PickupInformation v-if="isShipmentBOSSBOPSRETURN" :data="pickupAddress" />
          <UpsApInformation v-if="order?.upsApDetails" :data="order?.upsApDetails" />
          <ShippingInformation :data="shippingAddress" class="margin-bottom-m" />
          <GiftMessageCard v-if="order?.giftMessage" :message="order?.giftMessage" />
        </div>
        <div v-if="isS2AP" class="cell large-4 padding-right-xs margin-bottom-m hide-for-small-only">
          <picture v-if="storeMapUrl">
            <source media="(min-width: 192px)" :srcset="storeMapUrl" />
            <img alt="UPS access point store map" :src="storeMapUrl" loading="lazy" decoding="async" />
          </picture>
        </div>
        <PaymentInfoCard
          :payment="order?.payment"
          :show-view-all-transactions-btn="!!order?.tenders?.length"
          :s2ap="isS2AP"
          class="margin-bottom-m"
          @trigger-slideout="triggerSlideout()"
        />
        <div class="cell">
          <MarketPlaceSellerDetails v-if="showMarketplaceSellerDetails" :mkp-seller-ids="marketPlaceSellerIds" />
        </div>
      </div>
      <div v-if="showBarcodeSection" id="barcode-section" class="grid-x align-center align-middle cell margin-bottom-m padding-top-m border-top">
        <div class="barcode-container large-padding-left-xs large-padding-right-xs text-center">
          <div class="margin-bottom-xs">
            {{ ORDER_DETAILS.barcodeSection.barcodeItemText }}
          </div>
          <BarCode :barcode-number="order?.reservationNumber.toString()" do-pad />
          <NuxtLink :to="envProps.returnPolicyURL" external class="link-body">
            {{ ORDER_DETAILS.barcodeSection.barcodeReturnPolicyText }}
          </NuxtLink>
        </div>
      </div>
      <div v-if="isBigTicketOrder" class="cell small-12 medium-6 margin-bottom-m">
        <h2 class="h4 margin-bottom-xxs">
          {{ ORDER_DETAILS.bigTicket.furnitureHeading }}
        </h2>
        <p>{{ ORDER_DETAILS.bigTicket.furnitureNote }}</p>
        <NuxtLink
          class="secondary button button-small margin-top-m"
          :class="{ expanded: isMew }"
          :to="{ name: 'Order Status', query: { tab: 'furniture' } }"
          @click="scheduleDeliveryFireTag()"
        >
          {{ ORDER_DETAILS.bigTicket.scheduleButtonLabel }}
        </NuxtLink>
      </div>
      <NeedHelp
        class="text-center align-center padding-top-m border-top padding-bottom-m border-bottom"
        :shipment-type="order?.primaryShipmentType"
        show-chat
      />
      <MedalliaSurvey class="padding-top-m" :class="{ 'padding-bottom-m border-bottom': isMew }" />
    </div>
    <div v-if="!isLoading" class="cell small-12 medium-offset-1 medium-2">
      <ProsWrapper v-if="orderItems.length" data-context="OH_ZONE_A" :killswitches="orderDetails?.killSwitch" />
    </div>
    <OrderDetailsSkeleton v-if="isLoading" />
    <ProtectionPlanSlideout />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, defineAsyncComponent, markRaw } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import Notification from '@atomic-ui/notification/Notification';
import { useRoute } from 'vue-router';
import { UserStatus } from '@vue-util/cookie';
import { useTitle } from '@vueuse/core';
import OrderCardHeader from '@/components/common/OrderCardHeader';
import ProductThumbnailWrapper from '@/components/common/ProductThumbnailWrapper';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import ShippingInformation from '@/components/common/ShippingInformation';
import PickupInformation from '@/components/common/PickupInformation';
import GiftMessageCard from '@/components/common/GiftMessageCard';
import PaymentInfoCard from '@/components/common/PaymentInfoCard';
import MarketPlaceSellerDetails from '@/components/marketplace/MarketPlaceSellerDetails';
import { transformProducts } from '@/utils/TransformProduct';
import NeedHelp from '@/components/common/NeedHelp';
import BarCode from '@/components/common/BarCode';
import UpsApInformation from '@/components/common/UpsApInformation';
import OrderDetailsSkeleton from '@/components/skeleton/OrderDetailsSkeleton';
import { ORDER_DETAILS, brandName, CUSTOMER_SERVICE_BT, PPE_ERROR_TAG, ORDER_HISTORY } from 'config';
import { PPE_SCHEDULE_DELIVERY_URL } from '#shared/endpoints';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { useSlideout } from '@/composables/useSlideout';
import { useModal } from '@/composables/useModal';

// Defer non-critical and below-the-fold components to reduce initial bundle
const ProsWrapper = defineAsyncComponent(() => import('@/components/ProsWrapper'));
const MedalliaSurvey = defineAsyncComponent(() => import('@/components/MedalliaSurvey'));
const ProtectionPlanSlideout = defineAsyncComponent(() => import('@/features/ProductThumbnail/components/ProductThumbnail/ProtectionPlanSlideout'));

const route = useRoute();
const currentPage = 'OrderDetails';

const envPropsStore = useEnvPropsStore();
const orderDetailsStore = useOrderDetailsStore();

const { registerSlideout, toggleSlideout, closeSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();

const signedIn = computed(() => UserStatus.isSignedIn());

const orderDetails = computed(() => orderDetailsStore);
const killSwitches = computed(() => orderDetails.value?.killSwitch);
const order = computed(() => orderDetails.value?.order);
const isCancelOrderEligible = computed(() => order.value?.cancelOrderEligible);
const isLoading = computed(() => orderDetails.value?.isLoading);
const isBigTicketOrder = computed(() => order.value?.primaryShipmentType === 'BT');
const envProps = computed(() => envPropsStore.envProps);
const isMew = computed(() => envProps.value.isMew);
const storeMapUrl = computed(() => order.value?.upsApDetails?.storeMapUrl);
const shipments = computed(() => order.value?.shipments || []);
const orderItems = computed(() => {
  let items = [];
  order.value?.shipments?.forEach((shipment) => {
    items = items.concat(shipment.items);
  });
  return items;
});
const hasNotification = computed(() => order.value?.returnServiceDown || orderDetails.value?.orderDetailsError);

const isRedCarpetShipment = computed(() => {
  const envUpcNumber = envProps.value?.redCarpetUpcNumber;
  if (!killSwitches.value?.redCarpetMembershipEnabled) {
    return false;
  }

  if (!(orderItems.value && orderItems.value.length) || !envUpcNumber) {
    return false;
  }

  return orderItems.value.some((item) => item.upcNumber && envUpcNumber && `${item.upcNumber}` === `${envUpcNumber}`);
});

const isS2AP = computed(() => order.value?.upsApDetails && order.value.upsApDetails !== null);
const isShipmentBOSSBOPSRETURN = computed(() =>
  order.value?.shipments?.find(
    (shipment) =>
      shipment?.shipmentType?.toUpperCase() === 'BOSS' ||
      shipment?.shipmentType?.toUpperCase() === 'BOPS' ||
      shipment?.shipmentType?.toUpperCase() === 'RETURN' ||
      shipment?.exchangeType?.toUpperCase() === 'SSRE',
  ),
);
const pickupAddress = computed(() => ({
  ...(order.value?.pickUpAddress?.storeAddress || {}),
  ...(order.value?.pickUpAddress?.pickupDetails || {}),
  ...(order.value?.pickUpAddress || {}),
}));
const shippingAddress = computed(() => ({
  ...(order.value?.contact || {}),
  address: order.value?.contact?.orderDetailsAddresses,
}));
const ltlAttributes = computed(() => {
  const hasLtlItem = order.value?.shipments?.some((shipment) => shipment.items.some((item) => item.isLtlItem));
  if (!hasLtlItem) {
    return null;
  }
  const { ltlReturnReasons, havePackaging, timeForPickup, contactMethod } = order.value;

  return {
    ltlReturnReasons,
    havePackaging,
    timeForPickup,
    contactMethod,
  };
});
const updateOrderDetailsApiError = computed(() => orderDetails.value.updateOrderDetailsApiError);
const isBuyAgainSlideoutEnabled = computed(() => killSwitches.value?.buyAgainSlideoutEnabled);
const isSelfServiceReplacementExchangeEnabled = computed(() => killSwitches.value?.selfServiceReplacementExchangeEnabled);
const marketPlaceSellerIds = computed(() => {
  const mkpSellerIds = [];
  order.value?.shipments.forEach((shipment) => {
    shipment?.items.forEach((item) => {
      if (item && item.mkpLineItemDetails?.sellerId) {
        mkpSellerIds.push(item.mkpLineItemDetails?.sellerId);
      }
    });
  });
  return [...new Set(mkpSellerIds)];
});

const isOrderDataReady = ref(false);

const getOrders = async () => {
  const payload = {
    orderNumber: route.params.orderNumber,
    isSignedIn: signedIn,
    orderDetail: true,
    orderToken: route.query.o,
    locksFlag: route.query.locksFlag,
  };
  await orderDetailsStore.GET_ONLINE_ORDER_DETAILS({
    payload,
  });
  isOrderDataReady.value = true;
  if (updateOrderDetailsApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateOrderDetailsApiError.value.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateOrderDetailsApiError.value.message],
    });
  }
};

const shouldShowConfirmChoiceCta = (shipment) =>
  shipment.shipmentStatusCode === 111 && ['backordered_2a', 'backordered_2b'].includes(shipment.sortIdentifier) && !shipment.willingToWaitInd;

const shouldShowCancelItemCta = (shipment) =>
  shipment.shipmentStatusCode === 111 &&
  shipment.cancelItemEligibleFlag &&
  ((['backordered_2a', 'backordered_2b'].includes(shipment.sortIdentifier) && shipment.willingToWaitInd) ||
    !['backordered_2a', 'backordered_2b'].includes(shipment.sortIdentifier));

const shouldShowScheduleDeliveryCta = (shipment) => shipment.shipmentStatusCode === 201 && !signedIn.value;

const shouldShowConfirmShippingLink = (shipment) => shipment.sortIdentifier === 'bops_unavailableforpickup' && !signedIn.value;

const shouldShowTrackPackageLink = (shipment) =>
  shipment.shipmentType !== 'RETURN' && !order.value?.orderWithoutShipment && shipment?.exchangeType?.toLowerCase() !== 'ssre';

const getReturnDetailsLabel = (shipment) => {
  if (isSelfServiceReplacementExchangeEnabled.value && shipment?.exchangeType?.toLowerCase() === 'ssre' && shipment.type === 'SALE') {
    if (shipment.items[0]?.exchangeSubType?.toLowerCase() === 'exchange') {
      return ORDER_HISTORY.exchangeDetailsLabel;
    }
    return ORDER_HISTORY.replacementDetailsLabel;
  }
  return ORDER_DETAILS.orderInformation.returnDetailsText;
};

const getTrackPackageLabel = (shipment) => {
  if (['BOPS', 'BOSS', 'S2AP'].includes(shipment.shipmentType)) {
    return ORDER_DETAILS.orderInformation.moreInfoText;
  }
  return ORDER_DETAILS.orderInformation.trackPackageText;
};

const shouldShowShippingStatusNotification = (shipment) => shipment.shipmentStatusCode === 111 && shipment.willingToWaitInd;

const shouldShowReroutedNotification = (shipment) =>
  shipment.shipmentType === 'S2AP' && (shipment?.shipmentStatusCode === 604 || shipment?.shipmentStatusCode === 605) && shipment.shipmentReRouted;

const isPackageTrackable = (shipment) => shipment.shipmentType !== 'RETURN' && !order.value?.orderWithoutShipment;

const showMarketplaceSellerDetails = computed(() => killSwitches.value?.ppeVmpIntegrationEnabled && marketPlaceSellerIds.value.length > 0);
const showBarcodeSection = computed(
  () => order.value?.reservationNumber && !order.value?.orderLookupByZip && !isBigTicketOrder.value && !order.value?.mkpOrderWithAllMkpItems,
);

const medalliaSurveyInit = () => {
  const MEDALLIA_SURVEY = window.KAMPYLE_ONSITE_SDK;
  if (!MEDALLIA_SURVEY || typeof MEDALLIA_SURVEY.updatePageView !== 'function') return;
  MEDALLIA_SURVEY.updatePageView();
};

const getOrderItemsTaggingData = () => {
  const baseData = orderItems.value.reduce(
    (acc, item) => {
      acc.product_id.push(item?.productId ?? '');
      acc.product_upc.push(item?.upcNumber ?? '');
      return acc;
    },
    { product_id: [], product_upc: [] },
  );

  let protection_plan_eligible = [];
  let protection_plan_selected = [];

  if (orderItems.value.length > 0) {
    const isAnyEligible = orderItems.value.some((item) => item?.wnmPurchaseEligibility);
    const isAnySelected = orderItems.value.some((item) => item?.withProtectionPlan);
    protection_plan_eligible = [isAnyEligible ? 'true' : 'false'];
    protection_plan_selected = [isAnySelected ? 'true' : 'false'];
  }

  return {
    ...baseData,
    protection_plan_eligible,
    protection_plan_selected,
  };
};

const isUtagViewFired = ref(false);

watch(
  () => isOrderDataReady.value,
  (isOrderDataReady) => {
    if (isOrderDataReady && !isUtagViewFired.value) {
      const taggingData = getOrderItemsTaggingData();
      fireTag('view', {
        ...ORDER_DETAILS.orderDetailsTag,
        ...taggingData,
        customer_type: signedIn.value ? 'signed in' : 'guest',
      });
      isUtagViewFired.value = true;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  useTitle(`${ORDER_DETAILS.pageTitle} - ${brandName}`);
  if (!order.value) {
    await getOrders();
  }
  window.medalliaSurveyData = 'in Order History';
  medalliaSurveyInit();
});

onUnmounted(() => {
  orderDetailsStore.CLEAR_ORDER_DETAILS();
  closeSlideout('payment-details-slideout');
});

const cancelOrder = async () => {
  await registerModal({
    id: 'cancelOrderOverlay',
    title: ORDER_DETAILS.cancelOrderModalTitleText,
    width: '410px',
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/CancelOrderOverlay'))),
  });
  toggleModal('cancelOrderOverlay');
  fireTag('link', {
    ...ORDER_DETAILS.cancelOrderTag,
  });
};

const confirmChoice = async (shipment) => {
  fireTag('link', {
    ...ORDER_DETAILS.backOrderConfirmationTag,
  });
  await registerModal({
    id: 'confirmChoice',
    title: `${shipment.cancelItemEligibleFlag ? ORDER_DETAILS.confirmChoiceModal.yourChoiceText : ORDER_DETAILS.confirmChoiceModal.toWaitText}`,
    width: '410px',
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/BackOrderConfirmationOverlay'))),
  });

  const itemDetails = {
    itemName: shipment?.items[0]?.productName,
    reservationNumber: order.value?.reservationNumber,
    sortIdentifier: shipment.sortIdentifier,
    orderToken: shipment.orderToken,
    lineNumber: shipment.lineNumber,
    cancelItemEligibleFlag: shipment.cancelItemEligibleFlag,
    willingToWaitInd: shipment.willingToWaitInd,
    orderId: shipment?.items[0]?.orderId,
  };
  orderDetailsStore.SET_ITEM_DETAILS_OVERLAY(itemDetails);
  toggleModal('confirmChoice');
};

const confirmCancelItem = async (shipment) => {
  fireTag('link', {
    ...ORDER_DETAILS.backOrderCancelTag,
  });
  await registerModal({
    id: 'backOrderCancelConfirmationOverlay',
    title: ORDER_DETAILS.confirmCancelItemModalText,
    width: '410px',
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/BackOrderCancelConfirmationOverlay'))),
  });
  const itemDetails = {
    itemName: shipment?.items[0]?.productName,
    reservationNumber: order.value?.reservationNumber,
    sortIdentifier: shipment.sortIdentifier,
    orderToken: shipment.orderToken,
    lineNumber: shipment.lineNumber,
    orderId: shipment?.items[0]?.orderId,
  };
  orderDetailsStore.SET_ITEM_DETAILS_OVERLAY(itemDetails);
  toggleModal('backOrderCancelConfirmationOverlay');
};

const triggerSlideout = () => {
  registerSlideout({
    id: 'payment-details-slideout',
    title: ORDER_DETAILS.paymentDetailsTitle,
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/PaymentDetails'))),
  });
  toggleSlideout('payment-details-slideout');
};

const scheduleDelivery = async () => {
  window.open(PPE_SCHEDULE_DELIVERY_URL, '_self');
  fireTag('link', {
    site_action: ORDER_DETAILS.bigTicketOrderTag.site_action,
    action_location: ORDER_DETAILS.bigTicketOrderTag.action_location,
  });
};

const specialHandlingReturn = (product, shipment) => {
  const data = {
    sellerName: product.mkpLineItemDetails?.sellerName,
    partnerFulfillmentId: product.mkpLineItemDetails?.partnerFulfillmentId,
    lineNumber: shipment.lineNumber,
    mkpReturnReasons: product.mkpReturnReasons,
    orderNumber: order.value?.orderNumber,
    ...ltlAttributes.value,
  };
  registerSlideout({
    id: 'item-details-slideout',
    title: 'Special Handling Return',
    props: {
      data,
      isLtlItem: product.isLtlItem,
    },
    component: markRaw(defineAsyncComponent(() => import('@/components/marketplace/ContactSeller'))),
  });
  toggleSlideout('item-details-slideout');
};

const returnItemAction = (product, shipment) => {
  if (product.isLtlItem) {
    specialHandlingReturn(product, shipment);
    return;
  }
  const urlParams = [];
  if (shipment.orderToken) urlParams.push(`o=${encodeURIComponent(shipment.orderToken)}`);
  if (shipment.shipmentToken) urlParams.push(`cm_sp=${encodeURIComponent(shipment.shipmentToken)}`);
  urlParams.push('lid=return_items');
  if (product?.detail?.upcNumber) {
    urlParams.push(`upcnumber=${product.detail.upcNumber}`);
  }
  if (product.returnEligibleFlag) {
    window.location.href = `/my-orders/returns/select-items?${urlParams.join('&')}`;
  }
};

const trackPackage = () => {
  fireTag('link', {
    ...ORDER_DETAILS.orderDetailsTrackTag,
  });
};

const scheduleDeliveryFireTag = () => {
  fireTag('link', {
    ...ORDER_DETAILS.scheduleDeliveryTag,
  });
};
</script>
<style lang="scss" scoped>
.cancel-order-link {
  cursor: pointer;
}
.barcode-container {
  max-width: rem-calc(410);
}
.order-details-wrapper {
  .order-card-header {
    width: 100%;
  }
}
.sub-order-detail-wrapper {
  width: 100%;
}
</style>
